import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Fragment } from 'react'
import { cn } from '@/lib/cn'
import { lastVisited } from '@/hooks/useNavHistory'

export interface BreadcrumbItem {
  label: string
  href?: string
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[]
  className?: string
  /** When true (default), use white/translucent colors suited to dark hero bg. */
  dark?: boolean
}

export function Breadcrumbs({ items, className, dark = false }: BreadcrumbsProps) {
  if (!items || items.length === 0) return null
  const base = dark ? 'text-white/70 hover:text-white' : 'text-muted-foreground hover:text-foreground'
  const sep  = dark ? 'text-white/40' : 'text-content-placeholder'
  const last = dark ? 'text-white' : 'text-foreground'

  return (
    <nav className={cn('flex min-w-0 items-center gap-1 text-[13px] font-medium', className)} aria-label="Breadcrumb">
      {items.map((it, idx) => {
        const isLast = idx === items.length - 1
        return (
          <Fragment key={`${idx}-${it.label}`}>
            {idx > 0 && <ChevronRight className={cn('size-3.5 shrink-0', sep)} />}
            {it.href && !isLast ? (
              // Parent crumbs keep their natural width (capped) so a long current-page name
              // truncates instead of squeezing "Employees" down to "E…".
              <Link to={lastVisited(it.href) ?? it.href} title={it.label} className={cn('max-w-[45%] shrink-0 truncate transition-colors', base)}>
                {it.label}
              </Link>
            ) : (
              <span aria-current={isLast ? 'page' : undefined} title={it.label} className={cn('truncate', isLast ? `min-w-0 font-medium ${last}` : `max-w-[45%] shrink-0 ${base}`)}>
                {it.label}
              </span>
            )}
          </Fragment>
        )
      })}
    </nav>
  )
}
